import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EditableFieldBase } from '../editable-field-base';
import { ErrorsWarningsComponent } from '../errors-warnings/errors-warnings.component';

// TODO: change to ShowAsDisabled, ShowAsEnabled 
export enum InvalidValueMode {
  Prohibit,             // invalid value is not shown in the textbox or selection list 
  AllowDisabled,        // invalid value is shown in the selection list but is not selectable 
  AllowEnabled,         // invalid value is included with other valid values and may be selected 
  RemoveWhenDeselected, // invalid value is included with other valid values, but vanishes when deselected 
}

// This is recursive. A list option's value can be an array of list options (e.g. a group)
// The disabled option can be used to set individual items to disabled. Group headers will be
// disabled by default. Don't use this to manage invalid values, though -- that's done automatically
// using the InvalidValueMode. 
export class ListOption {
  public displayName: string = '';
  public value: ListOption[] | any = null;
  public disabled: boolean = false;
  public hidden: boolean = false;
  public id: string = '';

  public get isGroup(): boolean {
    return Array.isArray(this.value)
      && this.value[0] instanceof ListOption
  };

  constructor(displayName: string, value: any, disabled: boolean = false, hidden: boolean = false, id = '') {
    this.displayName = displayName;
    this.value = value;
    this.disabled = disabled;
    this.hidden = hidden;
    this.id = id;
  }

  // makes a deep recursive clone of the option 
  public clone(index: string = null): ListOption {
    var retVal = new ListOption(this.displayName, this.value, this.disabled, this.hidden, index);

    if (this.isGroup) {
      const opts = this.value as Array<ListOption>;

      const localList = opts.map((opt, subIndex) => {
        return opt.clone(`${this.displayName}_${subIndex}`);
      });

      retVal.value = localList;
    }

    return retVal; 
  }

  // Recursively search the options for the value 
  public contains(value: any): boolean {
    if (!this.isGroup) {
      if (this.disabled || this.hidden)
        return false;
      else 
        return this.value === value;
    }
    else {
      const foundVal = (this.value as Array<ListOption>).find(opt => (opt as ListOption).contains(value));
      return (foundVal != undefined); 
    }
  }

  // Recursively disables all values except the ones listed 
  public disableExcept(value: any[]): void {
    if (this.isGroup)
      (this.value as Array<ListOption>).forEach(opt => (opt as ListOption).disableExcept(value));
    else
      this.disabled = !value.find(item => item === this.value );
  }
}

@Component({
  selector: 'dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss', '../editable-fields.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltip, ErrorsWarningsComponent],
})
export class Dropdown extends EditableFieldBase implements OnInit,OnChanges {

  // Placeholder note: A placeholder text doesn't make sense for a multiselect listbox. (Ask paula)
  // It might make sense for a combo, but not for this.

  @Input() listOfOptions$: Observable<ListOption[]>; // the raw data to show in the list - either options or option groups
  @Input() invalidValueMode: InvalidValueMode
    = InvalidValueMode.AllowDisabled;               // how to handle invalid values
  @Input() maxSelectionCount: number = 1;           // how many items can be selected at the same time
  @Input() optionsToShow: number = 1;               // number of options to show. 1 = traditional dropdown. Lists larger
                                                    //    than this will get a scroll bar

  @Output() optionSelectEmitter$: EventEmitter<any> = new EventEmitter<any>();  // Emits when an option has been selected

  public optionList$: Observable<ListOption[]>;     // contains the calculated list of options (e.g. considers invalid values)
                                                    //    This is the property that the list is actually bound to
  private initialValue: any;                        // the initial value of the model, used for invalid entries 

  constructor() {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();

    //  All the actions to set the initial value and the optionsList$ need to occur in
    //  ngOnChanges so if the view changes (ex: changing between tabs of the same component)
    //  values and dropdown options will update accordingly.
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (!!changes['obj']) {
      // Hold onto the initial value because we need it for the invalid value display logic
      this.initialValue = this.obj[this.prop];

      this.optionList$ = combineLatest(this.listOfOptions$, this.value$)
        .pipe(
          //tap(val => console.log('╔═ optionList$', val)),
          map(([options, value]) => {
            // deep copy clone the list of options to make a one-time-use new list 
            let localList: ListOption[] = options.map((opt, index) => opt.clone(index.toString()));
            // Add an invalid value if necessary
            if (!options.find(opt => opt.contains(this.initialValue))) {
              switch (this.invalidValueMode) {
                case InvalidValueMode.AllowDisabled:
                case InvalidValueMode.AllowEnabled:
                case InvalidValueMode.RemoveWhenDeselected:
                  // Add the string representation of the invalid value to the top of the listbox,
                  // and optionally disable it so it can't be selected.
                  let invalidOption = new ListOption(this.initialValue, this.initialValue);
                  invalidOption.disabled = this.invalidValueMode == InvalidValueMode.AllowDisabled;

                  const removeFromList: boolean = (this.invalidValueMode == InvalidValueMode.RemoveWhenDeselected)
                    && (this.initialValue !== value);

                  if (!removeFromList)
                    localList.unshift(invalidOption);

                  break;
                case InvalidValueMode.Prohibit:
                  // Don't add it to the list 
                  break;
              }
            }

            // Disable everything else if we've reached the max selection
            if (Array.isArray(value)
              && (this.maxSelectionCount > 1)
              && (this.maxSelectionCount <= value.length)) {

              localList.forEach(opt => opt.disableExcept(value));
            }

            return localList;
          }),
          //tap(val => console.log('╚═ optionList$', val)),        
        );
    }
  }

  // Override base class onChange to intercept when too many items are selected.
  public onChange() {

    // Shift-select (marquee select) allows >1 item to be selected at the same time, so we need to trap
    // for this if this is a multi - select with a max selection count. We'll just slice it down to size.
    if (Array.isArray(this.obj[this.prop])
      && (this.maxSelectionCount > 1)
      && (this.maxSelectionCount < this.obj[this.prop].length)) {

      this.obj[this.prop] = this.obj[this.prop].slice(0, this.maxSelectionCount); // be sure we didn't marquee select more than allowed
    }

    this.optionSelectEmitter$.emit(this.obj[this.prop]);
    super.onChange();
  }
}
